'use client';

import React from 'react';
import { ChatState } from './ChatLayout';

interface ControlBarProps {
  chatState: ChatState;
  isMuted: boolean;
  isCameraOff: boolean;
  onSkip: () => void;
  onStart: () => void;
  onMuteToggle: () => void;
  onCameraToggle: () => void;
  onCancel: () => void;
  onReport: () => void;
}

export default function ControlBar({
  chatState,
  isMuted,
  isCameraOff,
  onSkip,
  onStart,
  onMuteToggle,
  onCameraToggle,
  onCancel,
  onReport,
}: ControlBarProps) {
  const isActive = chatState === 'chatting';
  const isSearching = chatState === 'searching' || chatState === 'connecting';

  return (
    <div className="h-14 md:h-16 bg-card border-t border-border flex items-center justify-between px-3 md:px-4 xl:px-8 flex-shrink-0 z-20">
      {/* Left — media controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={onMuteToggle}
          disabled={!isActive}
          title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
          className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center transition-all duration-150 active:scale-95 ${
            !isActive
              ? 'bg-muted text-muted-foreground opacity-40 cursor-not-allowed'
              : isMuted
              ? 'bg-destructive/10 text-destructive hover:bg-destructive/20' :'bg-muted text-foreground hover:bg-border'
          }`}
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 18, height: 18 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" clipRule="evenodd" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 18, height: 18 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6v12m0 0l-3-3m3 3l3-3M9 9l-3 3 3 3" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.07 4.929a10 10 0 010 14.142" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 5.636a10 10 0 000 14.142" />
            </svg>
          )}
        </button>

        <button
          onClick={onCameraToggle}
          disabled={!isActive}
          title={isCameraOff ? 'Turn camera on' : 'Turn camera off'}
          className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center transition-all duration-150 active:scale-95 ${
            !isActive
              ? 'bg-muted text-muted-foreground opacity-40 cursor-not-allowed'
              : isCameraOff
              ? 'bg-destructive/10 text-destructive hover:bg-destructive/20' :'bg-muted text-foreground hover:bg-border'
          }`}
          aria-label={isCameraOff ? 'Camera on' : 'Camera off'}
        >
          {isCameraOff ? (
            <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 18, height: 18 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          ) : (
            <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: 18, height: 18 }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.069A1 1 0 0121 8.876V15.124a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
            </svg>
          )}
        </button>
      </div>

      {/* Center — main action */}
      <div className="flex items-center gap-3">
        {(chatState === 'idle' || chatState === 'disconnected') && (
          <button
            onClick={onStart}
            className="control-btn bg-primary text-white px-4 md:px-6 py-2 md:py-2.5 text-sm hover:bg-primary/90 shadow-elevated"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="hidden md:inline">{chatState === 'disconnected' ? 'Find Next' : 'Start Chat'}</span><span className="md:hidden">{chatState === 'disconnected' ? 'Next' : 'Start'}</span>
          </button>
        )}

        {isSearching && (
          <button
            onClick={onCancel}
            className="control-btn bg-muted text-foreground px-4 md:px-6 py-2 md:py-2.5 text-sm hover:bg-border"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="hidden md:inline">Cancel Search</span><span className="md:hidden">Cancel</span>
          </button>
        )}

        {isActive && (
          <button
            onClick={onSkip}
            className="control-btn bg-primary text-white px-4 md:px-6 py-2 md:py-2.5 text-sm hover:bg-primary/90 shadow-elevated"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
            <span className="hidden md:inline">Next Stranger</span><span className="md:hidden">Next</span>
          </button>
        )}
      </div>

      {/* Right — report */}
      <div className="flex items-center gap-2">
        <button
          onClick={onReport}
          disabled={!isActive}
          title="Report this user for inappropriate behavior"
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-150 active:scale-95 ${
            isActive
              ? 'text-destructive hover:bg-destructive/10 border border-destructive/20 hover:border-destructive/40' :'text-muted-foreground opacity-40 cursor-not-allowed border border-transparent'
          }`}
          aria-label="Report user"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
          </svg>
          <span className="hidden sm:block">Report</span>
        </button>
      </div>
    </div>
  );
}